import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@cmp-src/primitives/Popover/Popover';
import { Calendar } from '@v2/components/ui/calendar';
import { addDays, endOfWeek, format, startOfWeek } from 'date-fns';

import React from 'react';
import { DateRange } from 'react-day-picker';
import { ArrowsGroup } from '../ArrowsGroup/ArrowsGroup';
import { CalendarTrigger } from './CalendarTrigger';

export const WeekSwitch = ({
  weekDate,
  nextWeek,
  prevWeek,
  setWeekDate
}: {
  weekDate: string;
  setWeekDate: (date: string) => void;
  nextWeek: () => void;
  prevWeek: () => void;
}) => {
  weekDate = weekDate.replace('Z', ''); // Remove the Z to make it a local date
  const [isOpen, setOpen] = React.useState(false);
  const _startOfWeek = startOfWeek(new Date(weekDate), { weekStartsOn: 1 });
  const [selectedWeek, setSelectedWeek] = React.useState<DateRange | undefined>(
    { from: _startOfWeek, to: endOfWeek(_startOfWeek, { weekStartsOn: 1 }) }
  );

  return (
    <div className="mb-2 flex w-full flex-row items-center justify-between gap-1 align-center">
      <div className="flex-grow">
        <Popover
          open={isOpen}
          // onOpenChange allows to close the popover when the user clicks outside of it
          onOpenChange={setOpen}
        >
          <PopoverTrigger className="w-full">
            <CalendarTrigger
              dates={[
                _startOfWeek,
                endOfWeek(_startOfWeek, { weekStartsOn: 1 })
              ]}
              separator={<>&nbsp;-</>}
              buttonProps={{
                onClick: () => setOpen(!isOpen),
                className:
                  'w-full rounded-[10px] hover:bg-neutral-50/50 hover:shadow-md hover:border-neutral-200 hover:transition-all hover:duration-200'
              }}
            />
          </PopoverTrigger>
          <PopoverContent className="rounded-lg" align="start">
            <Calendar
              className="border-0"
              required={true}
              mode="single"
              weekHover={true}
              showOutsideDays
              // @ts-expect-error The Calendar component expects a different type, but this works as intended
              selected={selectedWeek}
              disableApplyButtons={true}
              modifiers={{
                selected: selectedWeek,
                range_start: selectedWeek?.from,
                range_end: selectedWeek?.to,
                range_middle: (date: Date) => {
                  if (!selectedWeek?.from || !selectedWeek?.to) return false;
                  const time = date.getTime();
                  // Include start and end dates in the range_middle styling
                  return (
                    time >= selectedWeek.from.getTime() &&
                    time <= selectedWeek.to.getTime()
                  );
                }
              }}
              onDayClick={(day, modifiers) => {
                if (modifiers.selected) {
                  setSelectedWeek(undefined); // clear the selection if the day is already selected
                  return;
                }
                const from = startOfWeek(day, { weekStartsOn: 1 });
                const to = endOfWeek(day, { weekStartsOn: 1 });
                setSelectedWeek({
                  from,
                  to
                });
                setWeekDate(format(addDays(from, 1), 'yyyy-MM-dd'));
              }}
              onSelect={(range: any) => {
                setSelectedWeek(range);

                setWeekDate(
                  format(addDays(range.from ?? new Date(), 1), 'yyyy-MM-dd')
                );
                setTimeout(() => setOpen(false), 100);
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
      <div>
        <ArrowsGroup
          leftProps={{
            onClick: prevWeek
          }}
          rightProps={{
            onClick: nextWeek
          }}
        />
      </div>
    </div>
  );
};
